import { API, graphqlOperation } from 'aws-amplify';
import { getUser, getUserGroup } from '../graphql/queries';

/**
 * Get all pricing adjustments for a user based on their group memberships
 * @param {string} userId - The user ID to get group adjustments for
 * @returns {Array} Array of group adjustment objects
 */
export const getUserGroupAdjustments = async (userId) => {
  try {
    // Get the user to find their group membership using the new schema
    const userResult = await API.graphql(graphqlOperation(getUser, { id: userId }));
    const user = userResult.data.getUser;

    let adjustments = [];

    // Check if user has a group assignment via userGroupID and get group data directly
    if (user.userGroupID && user.userGroup) {
      const group = user.userGroup;
      
      if (group && (group.percentageAdjustment || group.dollarAdjustment)) {
        adjustments.push({
          id: group.id,
          title: group.title,
          percentageAdjustment: group.percentageAdjustment || 0,
          dollarAdjustment: group.dollarAdjustment || 0,
          adjustmentType: group.adjustmentType || 'percentage'
        });
      }
    }

    console.log(`Found ${adjustments.length} group pricing adjustments for user ${userId}:`, adjustments);
    return adjustments;

  } catch (error) {
    console.error('Error fetching user group adjustments:', error);
    return [];
  }
};

/**
 * Get the final pricing adjustments for a user, with group overriding individual offsets
 * Provider offsets are applied after group/user adjustments
 * @param {Object} userRecord - The user record with individual offset settings
 * @returns {Object} Final pricing configuration with either group or individual adjustments, plus provider offsets
 */
export const getUserPricingConfig = async (userRecord) => {
  try {
    // If userRecord already has userGroup data, use it directly to avoid extra API call
    let groupAdjustments = [];
    
    if (userRecord.userGroupID && userRecord.userGroup) {
      const group = userRecord.userGroup;
      if (group && (group.percentageAdjustment || group.dollarAdjustment)) {
        groupAdjustments.push({
          id: group.id,
          title: group.title,
          percentageAdjustment: group.percentageAdjustment || 0,
          dollarAdjustment: group.dollarAdjustment || 0,
          adjustmentType: group.adjustmentType || 'percentage'
        });
      }
    } else if (userRecord.userGroupID) {
      // If we only have userGroupID but not the full group data, fetch it
      groupAdjustments = await getUserGroupAdjustments(userRecord.id);
    }
    
    // Fetch provider offsets if user has a provider (createdByUserId)
    let providerAdjustments = [];
    if (userRecord.createdByUserId && userRecord.createdByUserId !== 'deleted') {
      try {
        const providerResult = await API.graphql(graphqlOperation(getUser, { id: userRecord.createdByUserId }));
        const provider = providerResult.data.getUser;
        
        if (provider && provider.priceOffsetType && provider.priceOffsetValue !== null && provider.priceOffsetValue !== undefined) {
          providerAdjustments.push({
            id: 'provider',
            title: `Provider Pricing (${provider.companyName || provider.email || 'Provider'})`,
            percentageAdjustment: provider.priceOffsetType === 'percentage' ? provider.priceOffsetValue : 0,
            dollarAdjustment: provider.priceOffsetType === 'fixed' ? provider.priceOffsetValue : 0,
            adjustmentType: provider.priceOffsetType
          });
          console.log('Found provider offsets:', providerAdjustments);
        }
      } catch (providerError) {
        console.error('Error fetching provider offsets:', providerError);
      }
    }
    
    // Combine adjustments: group/user adjustments first, then provider adjustments
    let allAdjustments = [];
    
    if (groupAdjustments.length > 0) {
      // User is in group(s) - use group adjustments, ignore individual offsets
      console.log('Using group pricing adjustments, ignoring individual user offsets');
      allAdjustments = [...groupAdjustments];
    } else if (userRecord.priceOffsetType && userRecord.priceOffsetValue !== null && userRecord.priceOffsetValue !== undefined) {
      // User not in group but has individual offsets
      console.log('Using individual user pricing offsets');
      allAdjustments = [{
        id: 'individual',
        title: 'Individual Pricing',
        percentageAdjustment: userRecord.priceOffsetType === 'percentage' ? userRecord.priceOffsetValue : 0,
        dollarAdjustment: userRecord.priceOffsetType === 'fixed' ? userRecord.priceOffsetValue : 0,
        adjustmentType: userRecord.priceOffsetType
      }];
    }
    
    // Add provider adjustments after user/group adjustments
    if (providerAdjustments.length > 0) {
      allAdjustments = [...allAdjustments, ...providerAdjustments];
    }
    
    if (allAdjustments.length > 0) {
      return {
        type: groupAdjustments.length > 0 ? 'group' : 'individual',
        adjustments: allAdjustments,
        hasGroupPricing: groupAdjustments.length > 0,
        hasProviderPricing: providerAdjustments.length > 0
      };
    } else {
      // No pricing adjustments
      console.log('No pricing adjustments found for user');
      return {
        type: 'none',
        adjustments: [],
        hasGroupPricing: false,
        hasProviderPricing: false
      };
    }
  } catch (error) {
    console.error('Error getting user pricing config:', error);
    return {
      type: 'none',
      adjustments: [],
      hasGroupPricing: false,
      hasProviderPricing: false
    };
  }
};

/**
 * Apply multiple pricing adjustments in a stacked manner
 * @param {number} basePrice - The original price
 * @param {Object} pricelistAdjustments - Pricelist-level adjustments
 * @param {Array} groupAdjustments - Array of group-level adjustments
 * @returns {number} The final adjusted price
 */
export const applyStackedAdjustments = (basePrice, pricelistAdjustments = {}, groupAdjustments = []) => {
  let price = parseFloat(basePrice);
  
  if (isNaN(price)) {
    console.warn('Invalid base price:', basePrice);
    return basePrice;
  }

  // First apply pricelist adjustments
  if (pricelistAdjustments.percentageAdjustment) {
    price = price * (1 + parseFloat(pricelistAdjustments.percentageAdjustment) / 100);
  }
  
  if (pricelistAdjustments.dollarAdjustment) {
    price = price + parseFloat(pricelistAdjustments.dollarAdjustment);
  }

  // Then apply group adjustments in order
  groupAdjustments.forEach((adjustment, index) => {
    if (adjustment.adjustmentType === 'percentage' || !adjustment.adjustmentType) {
      if (adjustment.percentageAdjustment) {
        price = price * (1 + parseFloat(adjustment.percentageAdjustment) / 100);
      }
    }
    
    if (adjustment.dollarAdjustment) {
      price = price + parseFloat(adjustment.dollarAdjustment);
    }
  });

  return Math.round(price * 100) / 100; // Round to 2 decimal places
};

/**
 * Process an entire pricelist with group adjustments
 * @param {Array} pricelistData - Array of pricelist items
 * @param {Object} pricelistAdjustments - Pricelist-level adjustments
 * @param {Array} groupAdjustments - Array of group-level adjustments
 * @returns {Array} Processed pricelist with adjusted prices
 */
export const processPricelistWithGroupAdjustments = (pricelistData, pricelistAdjustments = {}, groupAdjustments = []) => {
  if (!Array.isArray(pricelistData)) {
    console.warn('Invalid pricelist data provided');
    return pricelistData;
  }

  if (!groupAdjustments || groupAdjustments.length === 0) {
    console.log('No group adjustments to apply');
    return pricelistData;
  }

  console.log(`Processing ${pricelistData.length} items with ${groupAdjustments.length} group adjustments`);

  return pricelistData.map(item => {
    const originalPrice = parseFloat(item.PRICE || item.adjustedPrice || 0);
    const adjustedPrice = applyStackedAdjustments(originalPrice, pricelistAdjustments, groupAdjustments);
    
    return {
      ...item,
      groupAdjustedPrice: adjustedPrice,
      originalPrice: originalPrice
    };
  });
};

/**
 * Calculate a single price with group adjustments
 * @param {number} basePrice - The base price
 * @param {Array} groupAdjustments - Array of group adjustments
 * @returns {number} The adjusted price
 */
export const calculateGroupAdjustedPrice = (basePrice, groupAdjustments = []) => {
  return applyStackedAdjustments(basePrice, {}, groupAdjustments);
};

/**
 * Get pricing summary for display purposes
 * @param {number} originalPrice - Original price
 * @param {number} finalPrice - Final adjusted price
 * @param {Array} groupAdjustments - Applied group adjustments
 * @returns {Object} Pricing summary object
 */
export const getPricingSummary = (originalPrice, finalPrice, groupAdjustments = []) => {
  const totalAdjustment = finalPrice - originalPrice;
  const adjustmentPercentage = originalPrice > 0 ? ((totalAdjustment / originalPrice) * 100) : 0;
  
  return {
    originalPrice,
    finalPrice,
    totalAdjustment,
    adjustmentPercentage,
    hasAdjustments: groupAdjustments.length > 0,
    appliedGroups: groupAdjustments.map(adj => adj.title).join(', ')
  };
}; 